import { Initiative } from '../types/database';
import { askChatGPT } from '../hooks/useDatabase';

export interface Goal {
  id: string;
  user_id: string;
  category: 'financial' | 'professional' | 'personal';
  title: string;
  position: number;
  created_at: string;
  updated_at: string;
}

export interface InitiativeGoalScore {
  initiativeId: string;
  goalId: string;
  goalCategory: string;
  goalTitle: string;
  score: number; // 0-100
  reasoning: string;
}


/**
 * Scores how well an initiative aligns with a specific goal using ChatGPT
 */
export async function scoreInitiativeAgainstGoal(
  initiative: Initiative,
  goal: Goal
): Promise<InitiativeGoalScore> {
  try {
    const prompt = `Analyze how well this initiative advances the specified goal. Provide a score from 0-100 and detailed reasoning.

INITIATIVE:
Name: ${initiative.name}
Description: ${initiative.description}
Status: ${initiative.status}
Tags: ${initiative.tags?.join(', ') || 'None'}
Start Date: ${initiative.start_date}
End Date: ${initiative.end_date || 'Ongoing'}

GOAL:
Category: ${goal.category}
Title: ${goal.title}

Please provide your analysis in the following JSON format:
{
  "score": <number from 0-100>,
  "reasoning": "<detailed explanation of how this initiative does or doesn't advance the goal, considering factors like alignment, impact potential, feasibility, and relevance>"
}

Consider factors like:
- How directly the initiative's activities relate to the goal
- The potential impact on achieving the goal
- The initiative's current status and timeline
- Any tags or keywords that indicate alignment
- The overall strategic fit

Respond with ONLY the JSON object, no additional text.`;

    const response = await askChatGPT({
      userQuery: prompt,
      contacts: [],
      initiatives: [initiative],
      events: [],
      tasks: [],
      instructions: 'You are an expert business analyst who evaluates how well initiatives advance specific goals. Provide objective, data-driven assessments.'
    });

    // Parse the JSON response
    let parsedResponse;
    try {
      // Extract JSON from the response (in case there's extra text)
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        parsedResponse = JSON.parse(jsonMatch[0]);
      } else {
        throw new Error('No JSON found in response');
      }
    } catch (parseError) {
      console.error('Failed to parse ChatGPT response:', response);
      // Fallback scoring if parsing fails
      parsedResponse = {
        score: 50,
        reasoning: 'Unable to parse AI analysis. Default score assigned.'
      };
    }

    return {
      initiativeId: initiative.id,
      goalId: goal.id,
      goalCategory: goal.category,
      goalTitle: goal.title,
      score: Math.min(Math.max(parsedResponse.score || 50, 0), 100),
      reasoning: parsedResponse.reasoning || 'No reasoning provided'
    };
  } catch (error) {
    console.error('Error scoring initiative against goal:', error);
    return {
      initiativeId: initiative.id,
      goalId: goal.id,
      goalCategory: goal.category,
      goalTitle: goal.title,
      score: 0,
      reasoning: `Error during analysis: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}


/**
 * Score all initiatives against all goals using ChatGPT
 */
export async function scoreAllInitiativesAgainstGoals(
  initiatives: Initiative[],
  goals: Goal[],
  onProgress?: (current: number, total: number) => void
): Promise<InitiativeGoalScore[]> {
  const scores: InitiativeGoalScore[] = [];
  const total = initiatives.length * goals.length;
  let current = 0;
  
  for (const initiative of initiatives) {
    for (const goal of goals) {
      try {
        const score = await scoreInitiativeAgainstGoal(initiative, goal);
        scores.push(score);
        current++;
        if (onProgress) {
          onProgress(current, total);
        }
        
        // Add a small delay to avoid overwhelming the API
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error(`Error scoring initiative ${initiative.id} against goal ${goal.id}:`, error);
        // Add a fallback score
        scores.push({
          initiativeId: initiative.id,
          goalId: goal.id,
          goalCategory: goal.category,
          goalTitle: goal.title,
          score: 0,
          reasoning: `Error during analysis: ${error instanceof Error ? error.message : 'Unknown error'}`
        });
        current++;
        if (onProgress) {
          onProgress(current, total);
        }
      }
    }
  }
  
  return scores;
}

/**
 * Get the best scoring initiatives for a specific goal
 */
export function getTopInitiativesForGoal(
  scores: InitiativeGoalScore[],
  goalId: string,
  limit: number = 5
): InitiativeGoalScore[] {
  return scores
    .filter(score => score.goalId === goalId)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

/**
 * Get the best scoring goals for a specific initiative
 */
export function getTopGoalsForInitiative(
  scores: InitiativeGoalScore[],
  initiativeId: string,
  limit: number = 3
): InitiativeGoalScore[] {
  return scores
    .filter(score => score.initiativeId === initiativeId)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}
